import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "../router-compat";

// Assume these icons are imported from an icon library
import {
  BoxCubeIcon,
  ChevronDownIcon,
  GridIcon,
  HorizontaLDots,
  ListIcon,
  PieChartIcon,
  PlugInIcon,
  TableIcon,
  TimeIcon,
  UserCircleIcon,
} from "../icons";
import { useSidebar } from "../context/SidebarContext";
import { useLocale } from "@/shared/lib/i18n/locale-context";
import SidebarWidget from "./SidebarWidget";

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

type Dictionary = ReturnType<typeof useLocale>["t"];

/**
 * Where the panel parks itself when it is closed on a narrow window.
 *
 * ⚠️ **`translate-x` is a physical direction and the panel is on a logical edge.** It is pinned with
 * `start-0` — the RIGHT of the window in Arabic (§23.10) — and the template hides it with
 * `-translate-x-full`, which moves it LEFT. In Arabic that walks the closed sidebar its own width
 * INTO the page: at 820px it sat from x=240 to x=530, a column of icons floating over the branding
 * screen with nothing to explain it. It is not a mobile-only edge case either — the panel is used
 * on a laptop in a split window, which is exactly where it was found.
 *
 * ⚠️ Written per direction rather than as `-translate-x-full rtl:translate-x-full`, so the two never
 * both apply and the answer never depends on which variant Tailwind happens to emit last. Scoped
 * with `max-lg:` for the same reason: above that width the panel is always in place, and saying so
 * here removes the `lg:translate-x-0` that used to have to win an override fight to put it back.
 */
const Offscreen = "max-lg:ltr:-translate-x-full max-lg:rtl:translate-x-full";

/**
 * ⚠️ **The association's screens first, the template's own beneath them.**
 *
 * TailAdmin ships this list pointing at its demo pages — Ecommerce, Calendar, Form Elements. Those
 * are kept, because they are the catalogue: anyone opening the panel can see every component the
 * template provides, rendered in this product's own palette, without leaving the application. But
 * an operator opens this panel to work a search, and the four screens that do that belong above a
 * page called "Blank Page".
 *
 * ⚠️ **Built from the dictionary, not written here.** The template hard-codes English labels into
 * the module. §23.10 flips the panel between Arabic and English at runtime, so a label baked into
 * a nav item is a label that never translates — and `conventions.test.ts` refuses Arabic outside
 * `shared/config/locale/` precisely so this cannot be worked around by typing the Arabic in.
 *
 * ⚠️ Every item is shown to everyone, and the SERVER refuses what the role cannot do. §12.2 keeps
 * visibility data-driven — a nav item hidden on the panel's own idea of a role drifts from the API
 * the first time a policy changes, and the drift is invisible until somebody cannot find a screen
 * they are entitled to.
 *
 * ⚠️ **The mobile app deliberately does the opposite** (`Capabilities`, 24 August 2026) and both are
 * right for what they gate. This is the fine-grained question — which of several staff screens is
 * mine — where drift is the danger. There it is the coarse one: whether somebody is a volunteer at
 * all, which every new user answers "no" to until a human reviews their documents, and which the
 * server itself now uses to decide who gets called out. Neither should be "corrected" to match the
 * other.
 *
 * ⚠️ **This is the only navigation in the panel.** A second one lived in `widgets/SideNav.tsx` until
 * 25 August 2026 — same links, same §12.2 note, imported by nothing. It was edited twice in good
 * faith, once for the cross-reference above and once to add the audit trail, and neither change
 * appeared anywhere. Deleted rather than left as a trap.
 */
function buildNavItems(t: Dictionary): NavItem[] {
  return [
    { icon: <GridIcon />, name: t.nav.missions, path: "/missions" },
    { icon: <PieChartIcon />, name: t.nav.statistics, path: "/statistics" },
    { icon: <ListIcon />, name: t.nav.volunteers, path: "/volunteers" },
    { icon: <TableIcon />, name: t.nav.committee, path: "/committee" },
    { icon: <UserCircleIcon />, name: t.nav.users, path: "/users" },
    { icon: <PieChartIcon />, name: t.nav.points, path: "/points" },
    { icon: <PlugInIcon />, name: t.nav.committeeSettings, path: "/committee-settings" },
    { icon: <BoxCubeIcon />, name: t.nav.branding, path: "/branding" },
    { icon: <BoxCubeIcon />, name: t.nav.themes, path: "/themes" },
    { icon: <TimeIcon />, name: t.nav.audit, path: "/audit" },
    { icon: <TableIcon />, name: t.nav.exports, path: "/exports" },
  ];
}


const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const location = useLocation();
  const { t } = useLocale();

  const navItems = buildNavItems(t);


  const [openSubmenu, setOpenSubmenu] = useState<{
    type: "main" | "others";
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {}
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // const isActive = (path: string) => location.pathname === path;
  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname]
  );

  useEffect(() => {
    let submenuMatched = false;
    ["main"].forEach((menuType) => {
      const items = navItems;
      items.forEach((nav, index) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (isActive(subItem.path)) {
              setOpenSubmenu({
                type: menuType as "main" | "others",
                index,
              });
              submenuMatched = true;
            }
          });
        }
      });
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location, isActive]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number, menuType: "main" | "others") => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  const renderMenuItems = (items: NavItem[], menuType: "main" | "others") => (
    <ul className="flex flex-col gap-4">
      {items.map((nav, index) => (
        <li key={nav.name}>
          {nav.subItems ? (
            <button
              onClick={() => handleSubmenuToggle(index, menuType)}
              className={`menu-item group ${
                openSubmenu?.type === menuType && openSubmenu?.index === index
                  ? "menu-item-active"
                  : "menu-item-inactive"
              } cursor-pointer ${
                !isExpanded && !isHovered
                  ? "lg:justify-center"
                  : "lg:justify-start"
              }`}
            >
              <span
                className={`menu-item-icon-size  ${
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? "menu-item-icon-active"
                    : "menu-item-icon-inactive"
                }`}
              >
                {nav.icon}
              </span>
              {(isExpanded || isHovered || isMobileOpen) && (
                <span className="menu-item-text">{nav.name}</span>
              )}
              {(isExpanded || isHovered || isMobileOpen) && (
                <ChevronDownIcon
                  className={`ms-auto w-5 h-5 transition-transform duration-200 ${
                    openSubmenu?.type === menuType &&
                    openSubmenu?.index === index
                      ? "rotate-180 text-brand-500"
                      : ""
                  }`}
                />
              )}
            </button>
          ) : (
            nav.path && (
              <Link
                to={nav.path}
                className={`menu-item group ${
                  isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
                }`}
              >
                <span
                  className={`menu-item-icon-size ${
                    isActive(nav.path)
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                  }`}
                >
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span className="menu-item-text">{nav.name}</span>
                )}
              </Link>
            )
          )}
          {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
            <div
              ref={(el) => {
                subMenuRefs.current[`${menuType}-${index}`] = el;
              }}
              className="overflow-hidden transition-all duration-300"
              style={{
                height:
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? `${subMenuHeight[`${menuType}-${index}`]}px`
                    : "0px",
              }}
            >
              <ul className="mt-2 space-y-1 ms-9">
                {nav.subItems.map((subItem) => (
                  <li key={subItem.name}>
                    <Link
                      to={subItem.path}
                      className={`menu-dropdown-item ${
                        isActive(subItem.path)
                          ? "menu-dropdown-item-active"
                          : "menu-dropdown-item-inactive"
                      }`}
                    >
                      {subItem.name}
                      <span className="flex items-center gap-1 ms-auto">
                        {subItem.new && (
                          <span
                            className={`ms-auto ${
                              isActive(subItem.path)
                                ? "menu-dropdown-badge-active"
                                : "menu-dropdown-badge-inactive"
                            } menu-dropdown-badge`}
                          >
                            new
                          </span>
                        )}
                        {subItem.pro && (
                          <span
                            className={`ms-auto ${
                              isActive(subItem.path)
                                ? "menu-dropdown-badge-active"
                                : "menu-dropdown-badge-inactive"
                            } menu-dropdown-badge`}
                          >
                            pro
                          </span>
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    /* ⚠️ `border-e`, not the template's `border-r`. The panel is anchored to the START edge — the
       RIGHT one in Arabic (§23.10) — so the rule that separates it from the page is on its END side.
       A physical `border-r` drew the line down the window frame, where there is nothing to divide. */
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 start-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-e border-gray-200 
        ${
          isExpanded || isMobileOpen
            ? "w-[290px]"
            : isHovered
            ? "w-[290px]"
            : "w-[90px]"
        }
        ${isMobileOpen ? "" : Offscreen}`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`py-8 flex ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        {/* ⚠️ The association's mark, not TailAdmin's wordmark — and the panel's own name beside
            it. What shipped here was a picture of the template's logo, which is the single most
            visible thing on a screen the association is meant to recognise as theirs.

            ⚠️ The mark is `aria-hidden` and the name is real text: a logo image with `alt="Logo"`
            tells a screen-reader user nothing about which product they are in. */}
        <Link to="/missions" className="flex items-center gap-3">
          <img
            src={`${import.meta.env.BASE_URL}brand/aoun-symbol.png`}
            alt=""
            aria-hidden="true"
            className="size-9 shrink-0 object-contain"
          />
          {isExpanded || isHovered || isMobileOpen ? (
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-lg font-bold tracking-tight text-gray-800 dark:text-white/90">
                {t.app.title}
              </span>
              <span className="truncate text-theme-xs text-gray-500 dark:text-gray-400">
                {t.app.subtitle}
              </span>
            </span>
          ) : (
            <span className="sr-only">{t.app.title}</span>
          )}
        </Link>
      </div>
      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className="mb-6">
          <div className="flex flex-col gap-4">
            <div>
              <h2
                className={`mb-4 text-xs uppercase flex leading-[20px] text-gray-400 ${
                  !isExpanded && !isHovered
                    ? "lg:justify-center"
                    : "justify-start"
                }`}
              >
                {isExpanded || isHovered || isMobileOpen ? (
                  t.nav.sectionOperations
                ) : (
                  <HorizontaLDots className="size-6" />
                )}
              </h2>
              {renderMenuItems(navItems, "main")}
            </div>
          </div>
        </nav>
        {isExpanded || isHovered || isMobileOpen ? <SidebarWidget /> : null}
      </div>
    </aside>
  );
};

export default AppSidebar;




